import { withLDConsumer } from 'launchdarkly-react-client-sdk';
import React from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import { connect } from 'react-redux';
import { Field, Form, reduxForm, formValueSelector } from 'redux-form';
import { palette } from '@fortress-technology-solutions/fortress-component-library/design';
import {
  renderNumberField,
  renderSelectField,
  renderTextAreaField,
  renderTextField,
  validateIsInteger,
  validateIsPositive,
} from '../../../utils/redux-form-helper';
import { EDIT_FORM_NAME } from '../constants';
import messages from '../messages';
import validate from './validate';
import { useUnitTypesQuery } from './useUnitTypesQuery';
import { useCAUDesignationsQuery } from './useCAUDesignationsQuery';
import { formatCAUDesignationOptions } from '../../../utils/unitDesignation';

const formatUnitTypeDisplayText = (unitType) => {
  const { name, description } = unitType;
  return description ? `${name} - ${description}` : name;
};

function EditFloorPlanForm({
  intl,
  handleSubmit,
  onSubmit,
  minOccupants,
  isRDFloorPlan,
  isEligibleForUnitDesignation,
}) {
  const { unitTypes } = useUnitTypesQuery();
  const { cauDesignations, isLoading: isLoadingDesignations } =
    useCAUDesignationsQuery();

  const onHandleSubmit = (formValues) => {
    if (onSubmit) onSubmit(formValues);
  };

  const unitTypeOptions = (unitTypes || []).map((unitType) => ({
    value: unitType.id,
    text: formatUnitTypeDisplayText(unitType),
  }));

  const cauDesignationOptions = formatCAUDesignationOptions(cauDesignations);

  return (
    <Form onSubmit={handleSubmit(onHandleSubmit)}>
      <Field
        name="internalName"
        component={renderTextField}
        maxLength="40"
        label={intl.formatMessage(messages.internalName)}
        showLabel
      />
      <Field
        name="marketingName"
        component={renderTextField}
        maxLength="60"
        label={intl.formatMessage(messages.marketingName)}
        showLabel
      />
      {isEligibleForUnitDesignation && !isLoadingDesignations && (
        <div className="row">
          <div className="col-sm-12">
            <Field
              label={<FormattedMessage {...messages.unitDesignation} />}
              name="CAUDesignationId"
              component={renderSelectField}
              options={cauDesignationOptions}
            />
          </div>
        </div>
      )}
      <Field
        bsSize=""
        name="marketingDescription"
        component={renderTextAreaField}
        maxLength="100"
        label={intl.formatMessage(messages.marketingDescription)}
        showLabel
      />
      <div className="row">
        <div className="col-xs-12 col-sm-4">
          <Field
            name="nBeds"
            component={renderNumberField}
            label={intl.formatMessage(messages.bedrooms)}
            showLabel
            min="0"
            validate={[validateIsInteger, validateIsPositive]}
          />
        </div>
        <div className="col-xs-12 col-sm-4">
          <Field
            name="nBaths"
            component={renderNumberField}
            label={intl.formatMessage(messages.bathrooms)}
            showLabel
            min="0"
            validate={[validateIsInteger, validateIsPositive]}
          />
        </div>
        <div className="col-xs-12 col-sm-4">
          <Field
            name="nHalfBaths"
            component={renderNumberField}
            label={intl.formatMessage(messages.halfBathrooms)}
            showLabel
            min="0"
            validate={[validateIsInteger, validateIsPositive]}
          />
        </div>
      </div>
      <div className="row">
        <div className="col-xs-12 col-sm-7">
          <div className="">
            <label>
              <FormattedMessage {...messages.minMaxOccupants} />{' '}
            </label>
            <div className="row">
              <div className="col-sm-4">
                <Field
                  name="minOccupants"
                  component={renderNumberField}
                  min={'0'}
                  validate={[validateIsInteger, validateIsPositive]}
                />
              </div>
              <div
                className="col-sm-1"
                style={{ color: palette.light.lightGrey, fontSize: '20px' }}
              >
                /
              </div>
              <div className="col-sm-4">
                <Field
                  name="maxOccupants"
                  component={renderNumberField}
                  min={minOccupants}
                  validate={[validateIsInteger, validateIsPositive]}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {isRDFloorPlan && (
        <div className="row">
          <div className="col-sm-12">
            <Field
              label={<FormattedMessage {...messages.unitTypeLabel} />}
              name="unitTypeId"
              component={renderSelectField}
              options={unitTypeOptions}
              placeholder={intl.formatMessage(messages.selectUnitType)}
            />
          </div>
        </div>
      )}
    </Form>
  );
}

const selector = formValueSelector(EDIT_FORM_NAME);

const connnectedForm = connect((state: any) => ({
  initialValues: state.viewUnit.floorplanDetails,
  allFloorplans: state.manageFloorPlans.floorPlans,
  minOccupants: selector(state, 'minOccupants'),
}))(
  injectIntl(
    reduxForm({
      form: EDIT_FORM_NAME,
      touchOnChange: true,
      validate,
      enableReinitialize: true,
    })(EditFloorPlanForm),
  ),
);
export default withLDConsumer()(connnectedForm);
